import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Pagination } from '../../shared/model/pagination.model';
import { ApiService } from './api.service';
import { Role, Task, TaskGroup, User } from '../../shared/model/auth.model';
import { LocalService } from './local.service';
import { TenantDataMapping } from '../../shared/model/profile.model';
import { EmailTemplate, WorkingScheduleTemplate } from '../../shared/model/general.model';
import { Letter, LetterTemplate, ReportSetting, StaffAttendance, WorkingLocation } from '../../shared/model/managements.model';
import { UserPositions } from '../../shared/model/positions.model';

@Injectable({
  providedIn: 'root'
})
export class ManagementsService {

  private service = 'recruitment-http/personalia';
  private module = 'managements';

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private localService: LocalService,
  ) { }

  getUserList(data: Partial<User>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/users', { ...data, ...paginate });
  }

  activeDeactiveUser(userUuid: string) {
    return this.apiService.patch(this.service, 'api/v1/' + this.module + '/users/' + userUuid + '/status');
  }

  getRoleList(data?: Partial<Role>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/roles', { ...data, ...paginate });
  }

  getUserRoleList(userUuid: string, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + `/users/${ userUuid }/roles`, paginate);
  }

  saveUserRole(userUuid: string, role_id: number) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/users/${ userUuid }/roles`, {
      role_id: role_id,
    });
  }

  deleteUserRole(userUuid: string, role_id: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + `/users/${ userUuid }/roles/${ role_id }`);
  }

  adminCreateUser(data: Partial<User>[], lang = this.localService.getData('lang') || 'en') {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/users/create?lang=${ lang }`, data);
  }

  upsertRole(data: Partial<Role>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/roles', data);
  }

  deleteRole(roleId: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + '/roles/' + roleId);
  }

  getTaskList(paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/tasks', paginate);
  }

  upsertTaskGroup(data: TaskGroup) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/tasks/groups', data);
  }

  upsertTask(data: Task) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/tasks', data);
  }

  deleteTask(taskId: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + '/tasks/' + taskId);
  }

  deleteTaskGroup(taskGroupId: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + '/tasks/groups/' + taskGroupId);
  }

  getRoleTaskList(roleId: number, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + `/roles/${ roleId }/tasks`, paginate);
  }

  manageRoleTask(roleId: number, taskId: number, checked: boolean) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/roles/${ roleId }/tasks/${ taskId }`, {
      checked: checked,
    });
  }

  manageRoleTaskGroup(roleId: number, taskGroupId: number, checked: boolean) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/roles/${ roleId }/tasks/groups/${ taskGroupId }`, {
      checked: checked,
    });
  }

  manageRoleTaskAll(roleId: number, checked: boolean) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/roles/${ roleId }/tasks/all`, {
      checked: checked,
    });
  }

  getStaffDataMapping(paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/staff/data/mapping', paginate);
  }

  updateStaffDataMapping(data: Partial<TenantDataMapping>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/staff/data/mapping', data);
  }

  getEmailTemplateList(data?: Partial<EmailTemplate>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/email/template', { ...data, ...paginate });
  }

  upsertEmailTemplate(data: Partial<TenantDataMapping>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/email/template', data);
  }

  resetEmailTemplate(emailTemplateType: string) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + `/email/template/${emailTemplateType}/reset`);
  }

  getReportSetting(data?: Partial<ReportSetting>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/report/setting', { ...data, ...paginate });
  }

  upsertReportSetting(reportTypeId: number, data: Partial<ReportSetting>[]) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/report/${reportTypeId}/setting`, data);
  }
  
  getWorkingScheduleTemplateList(data?: Partial<WorkingScheduleTemplate>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/working-schedule/template', { ...data, ...paginate });
  }

  updateWorkingScheduleTemplate(scheduleTemplateId: number, data: Partial<WorkingScheduleTemplate>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/working-schedule/template/${scheduleTemplateId}`, data);
  }

  insertWorkingScheduleTemplate(data: Partial<WorkingScheduleTemplate>) {
    return this.apiService.put(this.service, 'api/v1/' + this.module + `/working-schedule/template`, data);
  }

  deleteWorkingScheduleTemplate(scheduleTemplateId: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + `/working-schedule/template/${scheduleTemplateId}`);
  }

  updateStaffWorkingSchedule(data: Partial<UserPositions>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/staff-working-schedule`, data);
  }

  createStaffAttendance(data: StaffAttendance) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + `/attend`, data);
  }

  getStaffLatestAttendance(allToday: boolean = false) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + `/attendance-latest/${allToday}`);
  }

  getWorkingLocationList(data?: Partial<WorkingLocation>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/working-location', { ...data, ...paginate });
  }

  upsertWorkingLocation(data: Partial<WorkingLocation>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/working-location', data);
  }

  deleteWorkingLocation(workingLocationId: number) {
    return this.apiService.delete(this.service, 'api/v1/' + this.module + '/working-location/' + workingLocationId);
  }

  upsertLetterTemplate(data: Partial<LetterTemplate>) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/letter/template', data);
  }

  getLetterTemplateList(data?: Partial<LetterTemplate>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/letter/template', { ...data, ...paginate });
  }

  getLetter(data?: Partial<Letter>, paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/letter', { ...data, ...paginate });
  }

  generateLetter(data: { letter_template_id: number, staff_placeholders: number[] }) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/letter/generate', data);
  }

  getAttendanceReport(paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + `/attendance/report`, paginate);
  }

  syncUserFromSIS() {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/sync/staff-account');
  }

  getBackgroundJobList(paginate?: Partial<Pagination>) {
    return this.apiService.get(this.service, 'api/v1/' + this.module + '/background-job', paginate);
  }

  switchAccount(userId: number) {
    return this.apiService.post(this.service, 'api/v1/' + this.module + '/switch-account', { target_user_id: userId });
  }
}
